/**
 * columnWidths.ts - Column width methods.
 *
 * Width changes do not alter cell values, so they persist with a `noop` table op.
 */

import type {TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {persistTable} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';

/**
 * Replace the table's column widths, checkpointing the current selection for undo first.
 */
export function setColumnWidths(params: TableMutationParams, widths: Array<number>): void {
  const {table, index, updateHandler} = params.getTableModel().props;
  saveSelection(params);
  persistTable(updateHandler, index, table.name, {table: {...table, widths}, tableOp: {type: 'noop'}});
}

/**
 * Set the width of the column at `columnIndex`. When that column lies within the selected column
 * range, every column in the range is resized to `width`.
 */
export function resizeColumns(params: TableMutationParams, columnIndex: number, width: number): void {
  const model = params.getTableModel();
  const columnSelection = model.selection.multiAxisDimSelection.column;
  const [start, end] =
    columnSelection && columnSelection[0] <= columnIndex && columnIndex <= columnSelection[1]
      ? columnSelection
      : [columnIndex, columnIndex];
  const widths = [...model.props.table.widths];
  for (let i = start; i <= end; i++) {
    widths[i] = width;
  }
  setColumnWidths(params, widths);
}

/**
 * Set the width of only the column at `columnIndex`, regardless of the current selection.
 */
export function setColumnWidth(params: TableMutationParams, columnIndex: number, width: number): void {
  const widths = [...params.getTableModel().props.table.widths];
  widths[columnIndex] = width;
  setColumnWidths(params, widths);
}
